import { useEffect } from "react";
import { Link } from "react-router-dom";

import { isCompanyFinanceEnabled } from "../../../config/companyFinance";
import { isCompanyOverviewEnabled } from "../../../config/companyOverview";
import { canAccessCompanyFinanceBooks } from "../lib/companyFinanceLabels";
import {
  companyStatusLabel,
  isActiveAdmin,
  isActiveMember,
  roleLabel,
} from "../lib/companyLabels";

function CompanyShellNav({
  companyId,
  company,
  membership,
  active,
  corporateClientsAvailable = false,
  corporatePropertiesAvailable = false,
}) {
  const showNav = Boolean(
    companyId && isActiveMember(company, membership),
  );
  const overviewEnabled = isCompanyOverviewEnabled();
  const financeEnabled = isCompanyFinanceEnabled();
  const showFinanceBooks =
    financeEnabled && canAccessCompanyFinanceBooks(company, membership);
  const showOwnRemuneration =
    financeEnabled && isActiveMember(company, membership);
  useEffect(() => {
    if (!showNav) return undefined;
    const nav = document.querySelector(".feature-company-workspace__nav");
    const current = nav?.querySelector("[aria-current='page']");
    if (!nav || !current) return undefined;
    // Prefer browser-native nearest alignment so the active tab stays visible
    // without expanding the Company shell or forcing aggressive centering.
    if (typeof current.scrollIntoView === "function") {
      current.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "auto",
      });
    }
    return undefined;
  }, [active, showNav, companyId]);
  if (!showNav) return null;
  const admin = isActiveAdmin(company, membership);
  // A3U: Property inventory READ is available to every ACTIVE member even when
  // corporate_properties_available (WRITE gate) is false. Unassigned queue
  // remains OWNER/ADMIN + write-available only.
  const items = [
    overviewEnabled
      ? {
          id: "overview",
          to: `/companies/${companyId}/overview`,
          label: "Огляд",
        }
      : null,
    { id: "profile", to: `/companies/${companyId}`, label: "Профіль" },
    admin
      ? {
          id: "settings",
          to: `/companies/${companyId}/settings`,
          label: "Налаштування",
        }
      : null,
    { id: "members", to: `/companies/${companyId}/members`, label: "Команда" },
    corporateClientsAvailable === true
      ? {
          id: "clients",
          to: `/companies/${companyId}/clients`,
          label: "Клієнти",
        }
      : null,
    {
      id: "properties",
      to: `/companies/${companyId}/properties`,
      label: "Об'єкти компанії",
    },
    {
      id: "tasks",
      to: `/companies/${companyId}/tasks`,
      label: "Задачі та перегляди",
    },
    showFinanceBooks
      ? {
          id: "finance",
          to: `/companies/${companyId}/finance`,
          label: "Фінанси компанії",
        }
      : null,
    showOwnRemuneration
      ? {
          id: "my-remuneration",
          to: `/companies/${companyId}/my-remuneration`,
          label: "Моя винагорода",
        }
      : null,
    admin && corporatePropertiesAvailable === true
      ? {
          id: "properties-unassigned",
          to: `/companies/${companyId}/properties/unassigned`,
          label: "Непризначені об'єкти",
        }
      : null,
    admin
      ? {
          id: "invitations",
          to: `/companies/${companyId}/invitations`,
          label: "Запрошення",
        }
      : null,
    admin
      ? {
          id: "join-requests",
          to: `/companies/${companyId}/join-requests`,
          label: "Заявки",
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="feature-company-workspace__nav-scroller">
      <nav
        className="feature-company-workspace__nav"
        aria-label="Розділи компанії"
      >
        {items.map((item) => (
          <Link
            key={item.id}
            to={item.to}
            aria-current={active === item.id ? "page" : undefined}
            className={[
              "feature-company-workspace__nav-link",
              active === item.id
                ? "feature-company-workspace__nav-link--active"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {item.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}

function CompanyPageHeader({
  title,
  subtitle,
  company,
  membership,
  actions,
  companyId,
  activeNav,
}) {
  const role = membership?.role || company?.my_role;
  const status = company?.status;

  return (
    <div className="feature-company-workspace__page-head">
      <header className="clients-header feature-company-workspace__header">
        <div className="clients-header__text">
          <h1 className="clients-header__title">{title}</h1>
          {subtitle ? <p className="clients-header__sub">{subtitle}</p> : null}
          {company ? (
            <p className="feature-company-workspace__meta">
              <span
                className="feature-company-workspace__company-name"
                title={company.name}
              >
                {company.name}
              </span>
              {status ? (
                <span> · {companyStatusLabel(status)}</span>
              ) : null}
              {role ? <span> · {roleLabel(role)}</span> : null}
            </p>
          ) : null}
        </div>
        {actions ? (
          <div className="clients-header__actions feature-company-workspace__header-actions">
            {actions}
          </div>
        ) : null}
      </header>
      <CompanyShellNav
        companyId={companyId || company?.id}
        company={company}
        membership={membership}
        active={activeNav}
        corporateClientsAvailable={
          company?.corporate_clients_available === true
        }
        corporatePropertiesAvailable={
          company?.corporate_properties_available === true
        }
      />
    </div>
  );
}

export default CompanyPageHeader;
